import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { SynonymAdminDialog } from '@/components/glossary/SynonymAdminDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  BookOpen, Search, Plus, CheckCircle2, Tag, Link2, ShieldCheck,
  LayoutGrid, Table, Pencil, Trash2, X, Loader2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { SaveWithVersionDialog } from '@/components/SaveWithVersionDialog';
import { useCan } from '@/lib/permissions';

export default function GlossaryPage() {
  const canWrite = useCan('glossary:write');
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // cards | table
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTerm, setEditTerm] = useState(null); // null = create, object = edit
  const [formTerm, setFormTerm] = useState('');
  const [formDef, setFormDef] = useState('');
  const [formCat, setFormCat] = useState('General');
  const [formSynonyms, setFormSynonyms] = useState('');
  const [formFields, setFormFields] = useState('');
  const [formMeasure, setFormMeasure] = useState('');
  const [measureOptions, setMeasureOptions] = useState([]);
  const [measureListOpen, setMeasureListOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [availablePacks, setAvailablePacks] = useState([]);
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [packsSaving, setPacksSaving] = useState(false);
  const [aiVocabOpen, setAiVocabOpen] = useState(false);

  useEffect(() => {
    api.getIndustryPacks()
      .then(r => { setAvailablePacks(r.available || []); setSelectedPacks(r.selected || []); })
      .catch(() => {});
  }, []);

  const togglePack = async (packId) => {
    const next = selectedPacks.includes(packId)
      ? selectedPacks.filter(p => p !== packId)
      : [...selectedPacks, packId];
    setSelectedPacks(next);
    setPacksSaving(true);
    try {
      const r = await api.setIndustryPacks(next);
      setSelectedPacks(r.selected || next);
      toast.success('Industry vocabulary updated');
    } catch (err) { toast.error(err.message || 'Failed to update packs'); }
    finally { setPacksSaving(false); }
  };

  const glIsDirty = dialogOpen && (formTerm.trim().length > 0 || formDef.trim().length > 0);
  const { confirmNavigation: glConfirmNav, UnsavedDialog: GlUnsavedDialog } = useUnsavedChanges(glIsDirty, 'You have unsaved glossary changes. Discard and leave?');

  const loadTerms = useCallback(async () => {
    try {
      const [t, c, m] = await Promise.all([
        api.getGlossary(search || undefined, filterCat || undefined),
        api.getGlossaryCategories(),
        api.getSemanticModels().catch(() => []),
      ]);
      setTerms(t);
      setCategories(c);
      setModels(m);
      api.getCertifiedMeasures().then(r => setMeasureOptions(r.measures || [])).catch(() => {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filterCat]);

  useEffect(() => { loadTerms(); }, [loadTerms]);

  // Deep-link: /glossary?term=<name> opens that term for editing (Glossary
  // Jump from the AI Explore Analysis Context panel). Runs once terms load.
  const [searchParams, setSearchParams] = useSearchParams();
  const jumpedRef = useRef(false);
  useEffect(() => {
    const want = searchParams.get('term');
    if (!want || jumpedRef.current || loading || !terms.length) return;
    const match = terms.find(t => (t.term || '').toLowerCase() === want.toLowerCase());
    if (match) { jumpedRef.current = true; openEdit(match); }
    // Clear the param so a refresh doesn't reopen it.
    const next = new URLSearchParams(searchParams); next.delete('term'); setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, loading]);

  const openCreate = () => {
    setEditTerm(null);
    setFormTerm(''); setFormDef(''); setFormCat('General'); setFormSynonyms(''); setFormFields(''); setFormMeasure('');
    setDialogOpen(true);
  };

  const openEdit = (term) => {
    setEditTerm(term);
    setFormTerm(term.term);
    setFormDef(term.definition || '');
    setFormCat(term.category || 'General');
    setFormSynonyms((term.synonyms || []).join(', '));
    setFormFields((term.related_fields || []).join(', '));
    setFormMeasure(term.measure_name || '');
    setDialogOpen(true);
  };

  const handleSave = async (versionDesc) => {
    setSaving(true);
    try {
      const data = {
        term: formTerm,
        definition: formDef,
        category: formCat,
        synonyms: formSynonyms.split(',').map(s => s.trim()).filter(Boolean),
        related_fields: formFields.split(',').map(s => s.trim()).filter(Boolean),
        measure_name: formMeasure.trim(),
      };
      let termId;
      if (editTerm) {
        await api.updateGlossaryTerm(editTerm.term_id, data);
        termId = editTerm.term_id;
      } else {
        const created = await api.createGlossaryTerm(data);
        termId = created.term_id;
      }
      setSaveDialogOpen(false);
      setDialogOpen(false);
      toast.success(editTerm ? 'Term updated & versioned' : 'Term created');
      loadTerms();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (termId) => {
    try {
      await api.deleteGlossaryTerm(termId);
      setTerms(prev => prev.filter(t => t.term_id !== termId));
      toast.success('Term deleted');
    } catch (err) { toast.error(err.message); }
  };

  const handleCertify = async (termId) => {
    try {
      await api.certifyTerm(termId);
      setTerms(prev => prev.map(t => t.term_id === termId ? { ...t, status: 'certified' } : t));
      toast.success('Term certified');
    } catch (err) { toast.error(err.message); }
  };

  return (
    <>
    <TooltipProvider>
      <div className="p-6 space-y-6" data-testid="glossary-page">
        <div className="flex items-center justify-between animate-slide-up">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Business Glossary</h1>
            <p className="text-sm text-muted-foreground mt-1">Standardized business terminology and definitions</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Industry vocabulary packs (global synonym layer) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="rounded-xl gap-2" data-testid="industry-packs-btn">
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  Industry Vocabulary
                  {selectedPacks.length > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1 border-cyan-500/30 text-cyan-300">{selectedPacks.length}</Badge>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-3" data-testid="industry-packs-panel">
                <p className="text-xs font-semibold text-foreground mb-1">Industry vocabulary packs</p>
                <p className="text-[11px] text-muted-foreground mb-3">Pick the domains that match your data. The AI expands acronyms (e.g. "DWP", "MRR") and understands common terms. Your glossary and model synonyms always take priority.</p>
                <div className="space-y-1.5 max-h-72 overflow-auto">
                  {availablePacks.map(p => (
                    <label key={p.id} className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-muted/30 cursor-pointer" data-testid={`industry-pack-${p.id}`}>
                      <Checkbox
                        checked={selectedPacks.includes(p.id)}
                        onCheckedChange={() => togglePack(p.id)}
                        disabled={packsSaving}
                        className="mt-0.5"
                        data-testid={`industry-pack-check-${p.id}`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs text-foreground">{p.name}</span>
                        <span className="block text-[10px] text-muted-foreground">{p.description}</span>
                      </span>
                    </label>
                  ))}
                  {availablePacks.length === 0 && <p className="text-[11px] text-muted-foreground/80 italic px-1.5">No packs available.</p>}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" className="rounded-xl gap-2" onClick={() => setAiVocabOpen(true)} data-testid="ai-vocab-btn">
              <Sparkles className="w-4 h-4 text-amber-400" />
              AI Vocabulary
            </Button>
            {/* View toggle */}
            <div className="flex items-center border border-border/50 rounded-lg overflow-hidden">
              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => setViewMode('cards')} className={`p-2 transition-colors ${viewMode === 'cards' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-cards-btn">
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </TooltipTrigger><TooltipContent side="bottom"><p className="text-xs">Card view</p></TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => setViewMode('table')} className={`p-2 transition-colors ${viewMode === 'table' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-table-btn">
                  <Table className="w-4 h-4" />
                </button>
              </TooltipTrigger><TooltipContent side="bottom"><p className="text-xs">Table view</p></TooltipContent></Tooltip>
            </div>
            {canWrite && (
            <Button variant="outline" className="rounded-xl gap-2" data-testid="seed-metric-aliases-btn"
              onClick={async () => {
                try {
                  const r = await api.seedMetricAliases();
                  toast.success(r.count ? `Added ${r.count} insurance metric alias${r.count === 1 ? '' : 'es'}` : 'Insurance metric aliases already present');
                  loadTerms();
                } catch (err) { toast.error(err.message || 'Failed to seed aliases'); }
              }}>
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              Seed Insurance Metrics
            </Button>
            )}
            {canWrite && (
            <Button onClick={openCreate} className="rounded-xl bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all active:scale-95" data-testid="add-term-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Term
            </Button>
            )}
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-3 animate-slide-up" style={{ animationDelay: '50ms' }}>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="glossary-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search terms, definitions, synonyms..." className="pl-10 rounded-xl bg-muted/50 border-border/50" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterCat('')} className={`px-3 py-1.5 rounded-lg text-xs transition-all ${!filterCat ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}>All</button>
            {categories.map(c => (
              <button key={c.category} onClick={() => setFilterCat(c.category)} className={`px-3 py-1.5 rounded-lg text-xs transition-all ${filterCat === c.category ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}>
                {c.category} ({c.count})
              </button>
            ))}
          </div>
        </div>

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children" data-testid="glossary-grid">
            {terms.map(term => (
              <div key={term.term_id} className="glass-card rounded-2xl p-5 hover:border-primary/20 transition-all group" data-testid={`term-${term.term_id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-primary/10 border border-primary/20"><BookOpen className="w-4 h-4 text-primary" strokeWidth={1.5} /></div>
                    <h3 className="text-sm font-heading font-semibold text-foreground">{term.term}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    {term.status === 'certified' ? (
                      <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> Certified</Badge>
                    ) : (
                      <Tooltip><TooltipTrigger asChild>
                        <button onClick={() => handleCertify(term.term_id)} className="opacity-0 group-hover:opacity-100 transition-opacity"><Badge variant="outline" className="text-[9px] cursor-pointer hover:bg-emerald-500/10"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Certify</Badge></button>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Mark as certified</p></TooltipContent></Tooltip>
                    )}
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={() => openEdit(term)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all" data-testid={`edit-term-${term.term_id}`}><Pencil className="w-3 h-3" /></button>
                    </TooltipTrigger><TooltipContent><p className="text-xs">Edit term</p></TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={() => handleDelete(term.term_id)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all" data-testid={`delete-term-${term.term_id}`}><Trash2 className="w-3 h-3" /></button>
                    </TooltipTrigger><TooltipContent><p className="text-xs">Delete term</p></TooltipContent></Tooltip>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{term.definition}</p>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[9px] border-border/30"><Tag className="w-2 h-2 mr-0.5" /> {term.category}</Badge>
                  {term.synonyms?.map(s => <Badge key={s} variant="outline" className="text-[9px] border-border/20 text-muted-foreground/80">{s}</Badge>)}
                </div>
                {term.related_fields?.length > 0 && (
                  <div className="mt-2 flex items-center gap-1 text-[9px] text-muted-foreground/70"><Link2 className="w-2.5 h-2.5" />{term.related_fields.join(', ')}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="glass-card rounded-2xl overflow-hidden" data-testid="glossary-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Term</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Definition</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Synonyms</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {terms.map(term => (
                  <tr key={term.term_id} className="border-b border-border/20 hover:bg-muted/20 transition-colors group" data-testid={`row-${term.term_id}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{term.term}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{term.definition}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-[9px]">{term.category}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground/80">{term.synonyms?.join(', ') || '-'}</td>
                    <td className="px-4 py-3">
                      {term.status === 'certified'
                        ? <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Certified</Badge>
                        : <Badge variant="outline" className="text-[9px]">{term.status || 'draft'}</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {term.status !== 'certified' && (
                          <Tooltip><TooltipTrigger asChild><button onClick={() => handleCertify(term.term_id)} className="p-1 rounded hover:bg-emerald-500/10 text-muted-foreground hover:text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Certify</p></TooltipContent></Tooltip>
                        )}
                        <Tooltip><TooltipTrigger asChild><button onClick={() => openEdit(term)} className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"><Pencil className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Edit</p></TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild><button onClick={() => handleDelete(term.term_id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Delete</p></TooltipContent></Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-foreground">{editTerm ? 'Edit Term' : 'Add Term'}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-xs text-muted-foreground">Term</Label><Input data-testid="term-name-input" value={formTerm} onChange={e => setFormTerm(e.target.value)} placeholder="e.g. Written Premium" className="rounded-lg bg-muted/50 border-border/50 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Definition</Label><textarea value={formDef} onChange={e => setFormDef(e.target.value)} placeholder="What this term means..." className="w-full rounded-lg bg-muted/50 border border-border/50 text-sm p-2 text-foreground mt-1 min-h-[80px]" /></div>
              <div><Label className="text-xs text-muted-foreground">Category</Label><Input value={formCat} onChange={e => setFormCat(e.target.value)} placeholder="e.g. Premium, Claims, Ratio" className="rounded-lg bg-muted/50 border-border/50 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Synonyms (comma-separated)</Label><Input value={formSynonyms} onChange={e => setFormSynonyms(e.target.value)} placeholder="e.g. GWP, Gross Written Premium" className="rounded-lg bg-muted/50 border-border/50 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Related Fields (comma-separated)</Label><Input value={formFields} onChange={e => setFormFields(e.target.value)} placeholder="e.g. written_premium_m" className="rounded-lg bg-muted/50 border-border/50 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Linked certified measure</Label>
                <div className="relative mt-1">
                  <Input data-testid="term-measure-input" value={formMeasure}
                    onChange={e => { setFormMeasure(e.target.value); setMeasureListOpen(true); }}
                    onFocus={() => setMeasureListOpen(true)}
                    onBlur={() => setTimeout(() => setMeasureListOpen(false), 150)}
                    placeholder="Search certified measures…" className="rounded-lg bg-muted/50 border-border/50" autoComplete="off" />
                  {measureListOpen && (() => {
                    const q = formMeasure.trim().toLowerCase();
                    const opts = measureOptions.filter(o => !q || (o.name || '').toLowerCase().includes(q) || (o.model_name || '').toLowerCase().includes(q));
                    return (
                      <div className="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border/60 bg-popover shadow-lg" data-testid="term-measure-list">
                        {opts.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-muted-foreground italic">No certified measures match.</div>
                        ) : opts.map((o, i) => (
                          <button type="button" key={`${o.model_id}-${o.name}-${i}`}
                            onMouseDown={() => { setFormMeasure(o.name); setMeasureListOpen(false); }}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/60 flex items-center justify-between gap-2"
                            data-testid={`term-measure-option-${i}`}>
                            <span className="font-medium text-foreground truncate">{o.name}</span>
                            <span className="text-[10px] text-muted-foreground truncate">{o.model_name}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <p className="text-[10px] text-muted-foreground/70 mt-1">Pick a certified measure so AI Explore treats this term as its alias and verifies answers resolve to it.{formMeasure && !measureOptions.some(o => o.name === formMeasure) ? ' ⚠ Not an exact certified measure name.' : ''}</p>
              </div>
              <Button onClick={() => setSaveDialogOpen(true)} disabled={saving || !formTerm.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="save-term-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : editTerm ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {editTerm ? 'Save Changes' : 'Create Term'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
    <GlUnsavedDialog />
    <SaveWithVersionDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen} onSave={handleSave} saving={saving} title={editTerm ? 'Update Term' : 'Create Term'} />
    <SynonymAdminDialog open={aiVocabOpen} onOpenChange={setAiVocabOpen} />
    </>
  );
}
